import { getValue } from 'firebase/remote-config';
import {
  DEFAULT_RATE_FLOOR_PERCENTAGES,
  type TRateFloorConfig,
} from '~/domain/Jobs/utils/rateFloor';
import { IUnlikelyShiftConfig } from '~/models/remoteConfig';
import { remoteConfig } from '../api/firebaseConfig';
import { tryCatch } from './TryCatch';

/**
 * Keys for Firebase Remote Config keys.
 * Can be used when calling {@link remoteConfig}'s `getValue()`
 *
 * @enum {string}
 */
const RemoteConfigKeys = {
  allowedWcStates: 'ALLOWED_WC_STATES',
  offerExpirationStates: 'OFFER_EXPIRATION_STATES',
  unlimitedApplicationEnabledStates: 'UNLIMITED_APPLICATION_ENABLED_STATES',
  statesToDisableTempRequests: 'BLOCKED_STATES',
  autofillMinAttendanceScore: 'AUTOFILL_MIN_ATTENDANCE_SCORE',
  unlikelyShiftConfiguration: 'UNLIKELY_SHIFT_CONFIG',
  bestInDentalNomineePopup: 'BEST_IN_DENTAL_OFFICE_NOMINEE_SURVEY_LINK',
  bestInDentalToolkitUrl: 'BEST_IN_DENTAL_TOOLKIT_LINK',
  officeTutorials: 'OFFICE_TUTORIAL_CONFIG',
  apiDefaultTimeoutMs: 'API_DEFAULT_TIMEOUT_MS',
  apiMaxRetryAttempts: 'API_MAX_RETRY_ATTEMPTS',
  apiRetryWhitelistedUrls: 'API_RETRY_WHITELISTED_URLS',
  chargeTimelineDays: 'CHARGE_TIMELINE_DAYS',
  chargeTimelineUserCreationDate: 'CHARGE_TIMELINE_USER_CREATION_DATE',
  closeJobFeedbackPositive: 'CLOSE_JOB_FEEDBACK_POSITIVE',
  closeJobFeedbackNegative: 'CLOSE_JOB_FEEDBACK_NEGATIVE',
  onsiteInterviewThresholdInMinutes: 'ONSITE_INTERVIEW_THRESHOLD_MINUTES',
  professionsByState: 'PROFESSIONS_BY_STATE',
  professionalsNearbyThreshold: 'PROFESSIONALS_NEARBY_THRESHOLD',
  rateFloorPercentages: 'RATE_FLOOR_PERCENTAGES',
  saltclickChatConfig: 'SALTCLICK_CHAT_CONFIG',
  csPhoneNumber: 'CS_PHONE_NUMBER',
};

/**
 * Saltclick (Salesforce Embedded Messaging) chat configuration. Shares the
 * SALTCLICK_CHAT_CONFIG key with mobile (which reads two extra WebView-only
 * fields). Remote config rather than `.env` so sandbox→production values can
 * change without a rebuild; there is deliberately no bundled default — an
 * unfetched config fails closed and the widget never loads.
 */
export interface ISaltclickChatConfig {
  /** Salesforce Service API (scrt2) host. */
  scrt2Url: string;
  /** 15-character Salesforce organization ID. */
  orgId: string;
  /** API name of the Embedded Service deployment. */
  deploymentName: string;
  /** Community site hosting the Embedded Messaging bootstrap script. */
  siteUrl: string;
}

export const RemoteConfigUtils = {
  getAllowedWcStates() {
    return getValue(remoteConfig, RemoteConfigKeys.allowedWcStates).asString();
  },

  getOfferExpirationStates() {
    return getValue(remoteConfig, RemoteConfigKeys.offerExpirationStates).asString();
  },

  getUnlimitedApplicationEnabledStates() {
    return getValue(remoteConfig, RemoteConfigKeys.unlimitedApplicationEnabledStates)
      ?.asString()
      ?.replace(/\s+/g, '')
      .split(',');
  },

  getDisabledTempPostStates() {
    const stateToDisableTempJobPost = getValue(
      remoteConfig,
      RemoteConfigKeys.statesToDisableTempRequests,
    )
      ?.asString()
      ?.replace(/\s+/g, '');

    return stateToDisableTempJobPost?.split(',');
  },

  getAutofillMinAttendanceScore() {
    return getValue(remoteConfig, RemoteConfigKeys.autofillMinAttendanceScore).asString();
  },

  getUnlikelyShiftConfiguration() {
    try {
      const unlikelyShiftConfiguration = getValue(
        remoteConfig,
        RemoteConfigKeys.unlikelyShiftConfiguration,
      ).asString();

      return unlikelyShiftConfiguration
        ? (JSON.parse(unlikelyShiftConfiguration) as IUnlikelyShiftConfig)
        : null;
    } catch {
      return null;
    }
  },

  getBestInDentalOfficeNomineeSurvey() {
    return getValue(remoteConfig, RemoteConfigKeys.bestInDentalNomineePopup).asString();
  },

  // Winner's Toolkit destination (GOTU-5630): Marketing's site today,
  // repointable to the toolkit. Read from RemoteConfig so BEST_IN_DENTAL_TOOLKIT_LINK
  // can override it per environment without a deploy; callers fall back to the
  // shared `BID_TOOLKIT_URL` default in `@tempmee/awards` when the key is unset.
  getBestInDentalToolkitUrl() {
    return getValue(remoteConfig, RemoteConfigKeys.bestInDentalToolkitUrl).asString();
  },

  getOfficeTutorials() {
    try {
      const tutorials = getValue(remoteConfig, RemoteConfigKeys.officeTutorials);

      if (tutorials) {
        return JSON.parse(tutorials.asString());
      }

      return [];
    } catch {
      return [];
    }
  },

  getApiDefaultTimeoutMs(): number {
    return getValue(remoteConfig, RemoteConfigKeys.apiDefaultTimeoutMs).asNumber();
  },

  getApiMaxRetryAttempts(): number {
    return getValue(remoteConfig, RemoteConfigKeys.apiMaxRetryAttempts).asNumber();
  },

  getApiRetryWhitelistedUrls(): string[] {
    try {
      const urls = getValue(remoteConfig, RemoteConfigKeys.apiRetryWhitelistedUrls);

      if (urls) {
        return JSON.parse(urls.asString());
      }

      return [];
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      return [];
    }
  },

  getChargeTimelineDays(): number {
    return getValue(remoteConfig, RemoteConfigKeys.chargeTimelineDays).asNumber();
  },

  getChargeTimelineUserCreationDate(): string {
    return getValue(remoteConfig, RemoteConfigKeys.chargeTimelineUserCreationDate).asString();
  },

  getCloseJobFeedbackPositive(): string[] {
    try {
      const positive = getValue(remoteConfig, RemoteConfigKeys.closeJobFeedbackPositive);

      if (positive) {
        return JSON.parse(positive.asString());
      }

      return [];
    } catch {
      return [];
    }
  },

  getCloseJobFeedbackNegative(): string[] {
    try {
      const negative = getValue(remoteConfig, RemoteConfigKeys.closeJobFeedbackNegative);

      if (negative) {
        return JSON.parse(negative.asString());
      }

      return [];
    } catch {
      return [];
    }
  },

  getOnsiteInterviewThresholdInMinutes(): number {
    const result = tryCatch(() => {
      return getValue(remoteConfig, RemoteConfigKeys.onsiteInterviewThresholdInMinutes);
    });

    if (!result.ok) {
      return 0;
    }

    return result.data.asNumber();
  },

  getProfessionsByState(): Record<string, string[]> {
    try {
      const value = getValue(remoteConfig, RemoteConfigKeys.professionsByState).asString();

      return value ? JSON.parse(value) : {};
    } catch {
      return {};
    }
  },

  getProfessionalsNearbyThreshold(): number {
    const result = tryCatch(() =>
      getValue(remoteConfig, RemoteConfigKeys.professionalsNearbyThreshold),
    );

    if (!result.ok) {
      return 0;
    }

    return result.data.asNumber();
  },

  getSaltclickChatConfig(): ISaltclickChatConfig | null {
    try {
      const value = getValue(remoteConfig, RemoteConfigKeys.saltclickChatConfig).asString();

      return value ? (JSON.parse(value) as ISaltclickChatConfig) : null;
    } catch {
      return null;
    }
  },

  // Shared with mobile; NumberUtils.getCSPhone normalises it and owns the fallback.
  getCSPhoneNumber(): string {
    return getValue(remoteConfig, RemoteConfigKeys.csPhoneNumber).asString();
  },

  getRateFloorPercentages(): TRateFloorConfig {
    try {
      const value = getValue(remoteConfig, RemoteConfigKeys.rateFloorPercentages).asString();
      const parsed: unknown = value ? JSON.parse(value) : undefined;

      return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
        ? (parsed as TRateFloorConfig)
        : DEFAULT_RATE_FLOOR_PERCENTAGES;
    } catch {
      return DEFAULT_RATE_FLOOR_PERCENTAGES;
    }
  },
};
